import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign, ShoppingBag, Clock, Truck, Wrench, Zap, FileText,
  CheckCircle2, HardHat, Wallet, AlertTriangle, Users, Package, Star, MessageSquareWarning,
} from "lucide-react";
import api from "@/lib/api";
import { formatFCFA } from "@/lib/format";
import { Loader } from "@/components/common";
import { PageTitle, Panel } from "./AdminShell";
import AdminQuickStock from "@/components/AdminQuickStock";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [low, setLow] = useState([]);
  const loadLow = useCallback(() => api.get("/admin/low-stock").then((r) => setLow(r.data)), []);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); loadLow(); }, [loadLow]);
  if (!d) return <Loader />;

  const kpis = [
    { icon: DollarSign, label: "Chiffre d'affaires", value: formatFCFA(d.all_revenue), accent: true, to: "/admin/commandes" },
    { icon: ShoppingBag, label: "Commandes", value: d.orders_count, to: "/admin/commandes" },
    { icon: Clock, label: "Commandes en attente", value: d.pending_orders, to: "/admin/commandes" },
    { icon: Truck, label: "À livrer", value: d.to_deliver, to: "/admin/livraisons" },
    { icon: Truck, label: "Livraisons du jour", value: d.deliveries_today, to: "/admin/livraisons" },
    { icon: Wrench, label: "Installations du jour", value: d.installs_today, to: "/admin/installations" },
    { icon: Zap, label: "Installations urgentes", value: d.urgent_installs, danger: d.urgent_installs > 0, to: "/admin/installations" },
    { icon: FileText, label: "Devis en attente", value: d.quotes_pending, to: "/admin/devis" },
    { icon: CheckCircle2, label: "Devis acceptés", value: d.quotes_accepted, to: "/admin/devis" },
    { icon: HardHat, label: "Professionnels actifs", value: d.pros_active, to: "/admin/professionnels" },
    { icon: Wallet, label: "Dû aux pros", value: formatFCFA(d.due_to_pros), warn: d.due_to_pros > 0, to: "/admin/paiements" },
    { icon: Wallet, label: "Payé aux pros", value: formatFCFA(d.paid_to_pros), to: "/admin/paiements" },
    { icon: AlertTriangle, label: "Stock faible", value: d.low_stock, danger: d.low_stock > 0, to: "/admin/catalogue" },
    { icon: Star, label: "Note moyenne avis", value: d.reviews_count > 0 ? `${d.reviews_average}★` : "—", to: "/admin/avis" },
    { icon: MessageSquareWarning, label: "Avis à valider", value: d.reviews_pending, warn: d.reviews_pending > 0, to: "/admin/avis" },
    { icon: Users, label: "Clients", value: d.clients_count, to: "/admin/clients" },
  ];

  return (
    <div>
      <PageTitle>Tableau de bord</PageTitle>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {kpis.map((k, i) => (
          <Link key={i} to={k.to} data-testid={`kpi-${i}`}
            className={`rounded-xl p-4 border transition hover:shadow-md hover:-translate-y-0.5 ${k.accent ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-slate-200"}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${k.accent ? "bg-[#E08B00]" : k.danger ? "bg-red-100" : k.warn ? "bg-amber-100" : "bg-[#FDF3EE]"}`}>
              <k.icon className={`w-5 h-5 ${k.accent ? "text-[#0F172A]" : k.danger ? "text-red-600" : k.warn ? "text-amber-600" : "text-[#C85A32]"}`} />
            </div>
            <p className={`font-num font-black text-xl ${k.accent ? "text-white" : "text-[#0F172A]"}`}>{k.value}</p>
            <p className={`text-xs ${k.accent ? "text-slate-300" : "text-slate-500"}`}>{k.label}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8">
        <h2 className="font-display text-xl font-bold text-[#0F172A] mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" /> Stock faible / rupture ({low.length})
        </h2>
        {low.length === 0 ? (
          <Panel className="p-5 text-sm text-slate-400">Aucun produit sous le seuil d'alerte. 👍</Panel>
        ) : (
          <Panel className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                <th className="text-left px-4 py-3">Produit</th>
                <th className="text-right px-4 py-3">Stock</th>
                <th className="text-right px-4 py-3">Seuil</th>
                <th className="text-right px-4 py-3">Réappro rapide</th>
              </tr></thead>
              <tbody>
                {low.map((p) => (
                  <tr key={p.id} className={`border-t border-slate-100 ${p.stock <= 0 ? "bg-red-50" : ""}`} data-testid={`lowstock-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-slate-100 overflow-hidden shrink-0">{(p.images || [])[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 m-2 text-slate-300" />}</div>
                        <span className="font-semibold text-slate-800 line-clamp-1">{p.name}</span>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-num font-bold ${p.stock <= 0 ? "text-red-600" : "text-amber-600"}`}>{p.stock <= 0 ? "Rupture" : `${p.stock} ${p.unit}`}</td>
                    <td className="px-4 py-3 text-right font-num text-slate-400">{p.alert_threshold}</td>
                    <td className="px-4 py-3"><div className="flex justify-end"><AdminQuickStock product={p} onDone={loadLow} /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}
      </div>
    </div>
  );
}
